/**
 * PlanItemDetail — the reviewable detail of one plan item (contracts 0.2.30 `PlanItemView`): the field values it sets, the
 * value-set entries, where in the code it changes, what it references, what could not be verified, what it was modelled on, and
 * which inputs the assistant chose rather than you.
 *
 * COLLAPSED BY DEFAULT AND BOUNDED. A plan can have thousands of items and each list is capped server-side at 200, so nothing here
 * renders until the reader opens "Details", and an opened list shows its first `DETAIL_PREVIEW` rows with "Show N more" for the rest.
 * An item with none of these fields renders nothing at all (an older server, or a plain item).
 */
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { FieldValueView, PlanItemView } from "@/services/multi-agent/laneV2/types";

/** Rows shown per list before "Show N more". */
export const DETAIL_PREVIEW = 6;

/** How many separate things the detail panel would show, 0 when the item has none. */
export function planItemDetailCount(item: PlanItemView): number {
  return (
    (item.field_values?.length ?? 0) +
    (item.value_set_pairs?.length ?? 0) +
    (item.change_sites?.length ?? 0) +
    (item.references?.length ?? 0) +
    (item.verification_gaps?.length ?? 0) +
    (item.model_supplied_input?.length ?? 0) +
    (item.derived_from ? 1 : 0)
  );
}

/** What a `derived_from.kind` code reads as; an unknown code is shown as the server sent it. */
export function derivedFromWords(kind: string | null | undefined): string {
  if (!kind) return "Modelled on";
  return `Modelled on (${kind.replace(/_/g, " ")})`;
}

/** Plain-language hint per known `needs_value` label code (MSA `value_missing`, `required_no_exemplar_value`). */
const NEEDS_VALUE_HINTS: Readonly<Record<string, string>> = {
  value_missing: "no value was given for it",
  required_no_exemplar_value: "required, and neither your request nor the record it is modelled on gives one",
};
/** Shown for a label code this client does not know; the raw label is never displayed. */
export const NEEDS_VALUE_GENERIC_HINT = "no value is known; the build will leave it empty unless you give one";

/** True when the entry is a "needs a value" marker. Decided by `status` only, never by an empty `value`. */
export function isNeedsValue(p: FieldValueView): boolean {
  return p.status === "needs_value";
}

export function needsValueHint(label: string | null | undefined): string {
  return (label != null && Object.prototype.hasOwnProperty.call(NEEDS_VALUE_HINTS, label) ? NEEDS_VALUE_HINTS[label] : undefined) ?? NEEDS_VALUE_GENERIC_HINT;
}

/** How many of an item's field-value entries still need a value. MSA does not send status on value_set_pairs as of RMV 17; tolerated. */
export function needsValueCount(item: PlanItemView): number {
  return (item.field_values ?? []).filter(isNeedsValue).length + (item.value_set_pairs ?? []).filter(isNeedsValue).length;
}

type Row = string | { text: string; needs: true };

function pairRow(p: FieldValueView): Row {
  return isNeedsValue(p) ? { text: `${p.field} — needs a value (${needsValueHint(p.label)})`, needs: true } : `${p.field} = ${p.value}`;
}

function DetailList({ title, rows }: { title: string; rows: readonly Row[] }) {
  const [all, setAll] = useState(false);
  if (rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, DETAIL_PREVIEW);
  const hidden = rows.length - shown.length;
  return (
    <div>
      <h5 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title} <span className="font-normal normal-case">({rows.length})</span>
      </h5>
      <ul className="mt-0.5 space-y-0.5">
        {shown.map((r, i) => (
          typeof r === "string" ? (
            <li key={i} className="break-words font-mono text-[11px] leading-snug text-foreground">
              {r}
            </li>
          ) : (
            <li key={i} data-needs-value="true" className="break-words rounded bg-amber-100 px-1 text-[11px] font-medium leading-snug text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              {r.text}
            </li>
          )
        ))}
      </ul>
      {hidden > 0 && (
        <button type="button" onClick={() => setAll(true)} className="mt-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground">
          Show {hidden} more
        </button>
      )}
    </div>
  );
}

export function PlanItemDetail({ item, defaultOpen = false, className }: { item: PlanItemView; defaultOpen?: boolean; className?: string }) {
  const [open, setOpen] = useState(defaultOpen);
  const count = planItemDetailCount(item);
  if (count === 0) return null;
  const needs = needsValueCount(item);
  return (
    <div data-lane-v2-plan-item-detail={item.item_id} className={cn("mt-0.5", className)}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
      >
        <span aria-hidden>{open ? "▾" : "▸"}</span>
        Details ({count})
        {needs > 0 && " "}
        {needs > 0 && <span className="font-semibold text-amber-700 dark:text-amber-300">· {needs} need{needs === 1 ? "s" : ""} a value</span>}
      </button>
      {open && (
        <div className="mt-1 space-y-2 border-l border-border/60 pl-2.5">
          <DetailList title="Sets" rows={(item.field_values ?? []).map(pairRow)} />
          <DetailList title="Value-set entries" rows={(item.value_set_pairs ?? []).map(pairRow)} />
          <DetailList title="Where it changes" rows={item.change_sites ?? []} />
          <DetailList title="Also uses" rows={(item.references ?? []).map((r) => `${r.metadata_type} · ${r.full_name}`)} />
          <DetailList title="Could not be verified" rows={item.verification_gaps ?? []} />
          {item.derived_from && (
            <div>
              <h5 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{derivedFromWords(item.derived_from.kind)}</h5>
              <p className="font-mono text-[11px] text-foreground">{item.derived_from.exemplar_full_name}</p>
            </div>
          )}
          <DetailList
            title="Chosen by the assistant"
            rows={(item.model_supplied_input ?? []).map(
              (m) => `${m.input_key} = ${m.value}${m.human === true ? " (you confirmed)" : m.human === false ? " (not confirmed by you)" : ""}`,
            )}
          />
        </div>
      )}
    </div>
  );
}
